import { useCallback, useEffect, useRef, useState } from 'react';

import type { ICollaboratorBenefit } from '../../interfaces/businessData';
import { BenefitsState, ICategorizedBenefits } from '../../interfaces/collaborators';
import Companies from '../../services/api/collaborators/collaborators';

interface IUseCollaboratorBenefitsProps {
  companyId: string;
  collaboratorEmail: string;
  enabled?: boolean;
}

export const useCollaboratorBenefits = ({
  companyId,
  collaboratorEmail,
  enabled = true
}: IUseCollaboratorBenefitsProps) => {
  const [allBenefitsState, setAllBenefitsState] = useState<BenefitsState>({
    data: {},
    isLoading: enabled,
    error: false
  });

  const activeRequestRef = useRef(0);

  const categorizeBenefits = (benefits: ICollaboratorBenefit[]): ICategorizedBenefits => {
    return benefits.reduce((acc, benefit) => {
      const category = benefit.category ?? 'uncategorized';
      if (!acc[category]) acc[category] = [];
      acc[category].push(benefit);
      return acc;
    }, {} as ICategorizedBenefits);
  };

  const fetchBenefits = useCallback(async () => {
    const requestId = ++activeRequestRef.current;
    setAllBenefitsState((prevState) => ({ ...prevState, isLoading: true }));

    const categorizedBenefits: ICategorizedBenefits = {};
    let hasError = false;

    try {
      const response = await Companies.getCollaboratorBenefits(companyId, collaboratorEmail);
      const collaboratorBenefits = categorizeBenefits(response.benefits);
      Object.entries(collaboratorBenefits).forEach(([category, benefits]) => {
        categorizedBenefits[category] = [...(categorizedBenefits[category] || []), ...benefits];
      });
    } catch {
      hasError = true;
    } finally {
      if (activeRequestRef.current === requestId) {
        setAllBenefitsState({
          data: hasError ? {} : categorizedBenefits,
          error: hasError,
          isLoading: false
        });
      }
    }
  }, [companyId, collaboratorEmail]);

  useEffect(() => {
    if (!enabled) {
      activeRequestRef.current++;
      setAllBenefitsState({ data: {}, isLoading: false, error: false });
      return;
    }
    fetchBenefits();
  }, [fetchBenefits, enabled]);

  return { allBenefitsState, refetch: fetchBenefits };
};
